import { RoleType } from './roles'

export type HardwareSupplierFilterKeyType = 'outlet' | 'city_plumbing' | string

export enum SUPPLIER_INTEGRATION_TYPES {
  FULL_INTEGRATION = 'full_integration',
  INDICATIVE_PRICING = 'indicative_pricing',
  PRICE_ON_APPLICATION = 'price_on_application',
  PRICE_ON_APPLICATION_WITH_TIERS = 'price_on_application_with_tiers',
}

export type SellerIntegrationType = {
  id: string
  name: string
  countries: string[]
  isEnabled: boolean // Whether the hardware distributor is connected to an org
  isConnected?: boolean // Whether the hardware distributor is connected to a the Seller. This is only required for when multi distributor is enabled.
  isDefault?: boolean // Whether the hardware distributor is the default distributor for the country.
  filterKey: HardwareSupplierFilterKeyType
  logoUrl: string
  description: string
  privacyPolicyUrl?: string
  termsOfUseUrl?: string
  accountManager?: RoleType
  currencySymbol?: string
  // This is the only permission on SupplierOrg that the FE cares about.
  isQuoteReviewEnabled?: boolean // Whether the supplier has quote review enabled
  stockLocations?: StockLocationType[]
  preferredStockLocation?: string
  customForms?: any[]
  integrationType?: SUPPLIER_INTEGRATION_TYPES
  allowsOrderCollection?: boolean
}

export const LEGACY_CUSTOM_DISTRIBUTORS = ['outlet', 'city_plumbing']

export type ComponentTypes = 'module' | 'other' | 'battery' | 'inverter' | 'custom'
export type ComponentTypesV2 = 'module' | 'battery' | 'inverter' | 'custom' | OtherComponentTypes

export const STANDARD_COMPONENT_TYPES_V2 = ['module', 'battery', 'inverter', 'custom']

export type OtherComponentTypes =
  | 'general'
  | 'dc_optimizer'
  | 'isolator'
  | 'meter'
  | 'cable'
  | 'battery_accessory'
  | 'battery_inverter'
  | 'inverter_accessory'
  | 'mounting_rail'
  | 'mounting_roof_anchor'
  | 'mounting_clamp'
  | 'mounting_flashing'
  | 'mounting_roof_fixing'
  | 'mounting_other'
  | 'ev_charger'
  | 'heat_pump'
  | 'vehicle'
  | 'subscription'

export type StockLocationType = {
  key: string
  name: string
  address: string
  contact_email?: string
  is_active?: boolean
}

export type StockLevelType = {
  planned_date?: string
  in_stock: boolean
  quantity: number
  location?: StockLocationType | null // if location is null, it is the Global Stock level for the supplier for all locations.
}

export interface StockLevelTypeV2 extends StockLevelType {
  is_available: boolean
}

type MinimumQuantityRequirementType = {
  greaterThanOrEqualToQuantity: string
}

export type MinimumAmountRequirementType = {
  greaterThanOrEqualToSubtotal: {
    amount: string
    currencyCode: string
  }
}

export type MinimumRequirementType = MinimumQuantityRequirementType | MinimumAmountRequirementType

export type FixedAmountDiscountValueType = {
  amount: { amount: string; currencyCode: string }
  appliesOnEachItem: boolean
}
export type PercentageDiscountValueType = { percentage: number }

type DiscountGenericType<T extends FixedAmountDiscountValueType | PercentageDiscountValueType> = {
  combinesWith: { orderDiscounts: boolean; productDiscounts: boolean; shippingDiscounts: boolean }
  customerGets: {
    appliesOnOneTimePurchase: boolean
    appliesOnSubscription: boolean
    value: T
  }

  discountClass: 'PRODUCT'
  endsAt?: Date
  startsAt: Date
  minimumRequirement: MinimumRequirementType
  id: string
  codes?: string
  shortSummary: string
  summary: string
  title: string
}

export type FixedAmountDiscountType = DiscountGenericType<FixedAmountDiscountValueType>

export type PercentageDiscountType = DiscountGenericType<PercentageDiscountValueType>

export type DiscountType = PercentageDiscountType | FixedAmountDiscountType
export type ProductDocumentType = {
  description: string
  Warranty: string
  url: string
}
export type DistributorDataType = {
  distributor: HardwareSupplierFilterKeyType
  image_url?: string
  variant_id: string
  price: number
  locations: string[]
  quantity_per_item: number
  is_available: boolean
  is_in_stock?: boolean
  stock_levels: StockLevelType[] | undefined
  product_documents: ProductDocumentType[]
  volume_discounts: DiscountType[]
  refresh_date: string
  distributor_variant_id: string
  short_description?: string
  price_detail: {
    price_ex_vat: number
    discount_value: number
    minimum_quantity: number
  }
  distributor_sku?: string
  deals: unknown[]
}

export interface DistributorDataTypeV2 extends DistributorDataType {
  bulk_options: BulkOptionType[]
  code: string
  image_url: string
  stock_levels: StockLevelTypeV2[]
  title: string
  short_description: string
  description: string
  currency_symbol: string
  price_detail: {
    price_ex_vat: number
    discount_value: number
    minimum_quantity: number
  }
}

export type BulkOptionType = {
  quantity_per_item: number
}

// The Endpoint documentation here: https://opensolar.slab.com/posts/15964-documentation-componentsv-2-endpoint-bglvgyxw
export interface ComponentLikeType {
  code: string
  name: string
  manufacturer_name: string
  id: number
  model_id: number
  rating: number
  is_exhibitor: boolean
  is_my_list: boolean
  is_archived?: boolean
  type: ComponentTypesV2
  ordering_v2: DistributorDataTypeV2[]
  manufacturer_logo?: string
  promotional_image?: string
}

export type BomLineItemType = {
  id?: string
  code: string
  componentType: ComponentTypes
  componentTypeV2: ComponentTypesV2
  quantity: number
  source: string // Could be a hardware distributor filter key OR a non-distributor source.
}

export type Bom = {
  systemBomId?: number
  systemUuid: string
  projectId: number
  lineItems: BomLineItemType[]
}

export const NON_DISTRIBUTOR_SOURCE_COST = ['component', 'manual', 'unknown']
